import { useEffect, useRef } from 'react';
import TurndownService from 'turndown';
import Quill, { Delta } from 'quill';
import { useEmailContext } from 'features/email-generator/EmailGeneratorContext';
// Import LinkBlot from blog writer (or create a copy in email-generator)
import LinkBlot from '../../../ai-blog-writer/components/BlogWriter/LinkBlot';
import { removeMarkdown } from 'lib/utils';

interface EditEmailProps {
  content: string;
  onQuillRef: (quill: Quill) => void;
  onActiveFormatsChange: (formats: {
    [key: string]: boolean | number | string | unknown;
  }) => void;
  onWordCountChange: (count: number) => void;
  activeIndex: number;
}

export const convertToMarkdown = (htmlInput: string) => {
  try {
    const turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      emDelimiter: '_',
      bulletListMarker: '-',
      hr: '---',
    });

    // Add additional rules for better conversion
    turndownService.addRule('superscript', {
      filter: ['sup'],
      replacement: function (content) {
        return '^' + content;
      },
    });

    turndownService.addRule('subscript', {
      filter: ['sub'],
      replacement: function (content) {
        return '~' + content;
      },
    });

    const markdown = turndownService.turndown(htmlInput);

    return markdown;
  } catch (err) {
    console.log(err);
    return '';
  }
};

const EditEmail = ({
  content,
  onQuillRef,
  onActiveFormatsChange,
  onWordCountChange,
  activeIndex,
}: EditEmailProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const lastIndexRef = useRef<number>(activeIndex);
  const { updateEmailAtIndex } = useEmailContext();
  const calculateWordCount = (text: string): number => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  function lineBreakMatcher() {
    const newDelta = new Delta();
    newDelta.insert({ break: '' });
    return newDelta;
  }

  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      // Register custom blots
      Quill.register('formats/link', LinkBlot);

      quillRef.current = new Quill(editorRef.current, {
        modules: {
          toolbar: false,
          history: {
            delay: 400,
            maxStack: 500,
            userOnly: true,
            boundaries: true,
          },
          clipboard: {
            matchVisual: false,
            matchers: [['BR', lineBreakMatcher]],
          },
        },
        formats: [
          'bold',
          'italic',
          'underline',
          'strike',
          'link',
          'list',
          'header',
          'indent',
          'color',
          'background',
          'table',
          'check',
          'image',
        ],
        theme: 'snow',
      });

      onQuillRef(quillRef.current);

      quillRef.current.on('text-change', () => {
        if (!quillRef.current) return;

        const text = quillRef.current.getText();
        const newWordCount = calculateWordCount(text);
        onWordCountChange(newWordCount);

        const newContent = quillRef.current.root.innerHTML;

        if (lastIndexRef.current !== activeIndex) {
          updateEmailAtIndex(activeIndex, newContent);
          lastIndexRef.current = activeIndex;
        }

        const selection = quillRef.current.getSelection();
        if (selection) {
          onActiveFormatsChange(
            quillRef.current.getFormat(selection) as {
              [key: string]: boolean | number | string | unknown;
            }
          );
        }
      });
    }
  }, [activeIndex, content]);

  useEffect(() => {
    if (quillRef.current && content) {
      const currentContent = quillRef.current.root.innerHTML;
      // Remove markdown code block delimiters if present
      const cleanContent = removeMarkdown(content);

      if (currentContent !== cleanContent) {
        quillRef.current.root.innerHTML = cleanContent;
        const text = quillRef.current.getText();
        const wordCount = calculateWordCount(text);
        onWordCountChange(wordCount);
      }
    }
  }, [content]);

  // Add click handler for links
  useEffect(() => {
    const handleLinkClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A') {
        e.preventDefault();
        const href = target.getAttribute('href');
        if (href) {
          // Ensure URL is absolute
          let absoluteUrl = href;
          if (!/^https?:\/\//i.test(href)) {
            absoluteUrl = 'https://' + href;
          }
          window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
        }
      }
    };

    // Use the current editor instance instead of querying the document
    if (quillRef.current) {
      const editorElement = quillRef.current.root;
      editorElement.addEventListener('click', handleLinkClick);

      return () => {
        editorElement.removeEventListener('click', handleLinkClick);
      };
    }

    return undefined;
  }, []);

  return (
    <div className="relative">
      {/* {isGenerating ? (
      <div className="absolute w-full h-full bg-white z-[999]">
        <ArticleSkeleton />
      </div>
    ) : null} */}
      <div className="max-w-5xl h-full rounded-lg mx-auto prose prose-lg relative flex flex-col">
        <div className="flex-grow overflow-auto">
          <div ref={editorRef} className="h-full" />
        </div>
      </div>
      <style>{`
        .ql-container.ql-snow {
          border: none;
          font-family: inherit;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .ql-editor {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          gap: 4px;
          
          overflow-y: auto;
          min-height: calc(100vh - 280px);
          max-height: calc(100vh - 280px);
        }
        
        .ql-editor::-webkit-scrollbar {
          width: 8px;
        }
        
        .ql-editor::-webkit-scrollbar-track {
          background: background-main;
          border-radius: 4px;
        }
        
        .ql-editor::-webkit-scrollbar-thumb {
          background: primary;
          border-radius: 4px;
        }
        
        .ql-editor::-webkit-scrollbar-thumb:hover {
          background: primary;
        }

        .ql-editor h1 {
          font-size: 2.25rem;
          color: hsl(var(--text-primary));

          font-weight: bold;
        }
        
        .ql-editor h2 {
          font-size: 1.875rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h3 {
          font-size: 1.5rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h4 {
          font-size: 1.25rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h5 {
          font-size: 1.125rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h6 {
          font-size: 1rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor p {
          font-size: 1.125rem;
          line-height: 1.75;
          color: hsl(var(--text-primary));
        }

        .ql-editor img {
          max-width: 100%;
          height: auto;
        }
        
        .ql-editor table {
          border-collapse: collapse;
          width: 100%;
        }
        
        .ql-editor td {
          border: 1px solid #cbd5e1;
          padding: 8px;
        }

        .ql-editor ul,
        .ql-editor ol {
          padding-left: 1.5em;
        }

        .ql-editor li {
          margin-bottom: 0.5em;
        }

        .ql-editor a {
          color: hsl(var(--text-primary));
          text-decoration: underline;
          cursor: pointer;
        }

        .ql-editor strong {
          color: hsl(var(--text-primary));
        }

        .ql-editor blockquote {
          border-left: 4px solid #e5e7eb;
          margin: 1.5em 0;
          padding-left: 1em;
          color: hsl(var(--text-primary));;
        }

        .ql-editor pre {
          background: #f3f4f6;
          padding: 1em;
          border-radius: 4px;
          overflow-x: auto;
        }

        .ql-editor code {
          background: #f3f4f6;
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-family: ui-monospace, monospace;
        }
      `}</style>
    </div>
  );
};

export default EditEmail;
